import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackPixel } from '../utils/metaPixel';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isOpen, setIsOpen] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(5.00); // Default to first tier (1km=5€)
  const [distanceKm, setDistanceKm] = useState(null);
  const [isBeachMode, setIsBeachMode] = useState(false);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // Beach-mode keeps a flat surcharge ON TOP of the distance-based fee.
  // We no longer reset deliveryFee here — CheckoutPage asks the backend for the
  // real fee (POST /api/delivery/quote) once the customer drops a pin on the map.

  const addItem = (item) => {
    trackPixel('AddToCart', {
      content_ids: [item.item_id],
      content_name: item.name,
      content_type: 'product',
      value: item.price,
      currency: 'EUR',
    });
    setItems(prev => {
      const existing = prev.find(i => i.item_id === item.item_id);
      if (existing) {
        return prev.map(i => 
          i.item_id === item.item_id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeItem = (itemId) => {
    setItems(prev => prev.filter(i => i.item_id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    setItems(prev => prev.map(i => 
      i.item_id === itemId ? { ...i, quantity } : i
    ));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('cart');
  };

  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const total = subtotal + deliveryFee;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Get unique restaurant from cart
  const restaurantId = items.length > 0 ? items[0].restaurant_id : null;
  const restaurantName = items.length > 0 ? items[0].restaurant_name : null;

  const value = {
    items,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    subtotal,
    deliveryFee,
    setDeliveryFee,
    distanceKm,
    setDistanceKm,
    total,
    itemCount,
    restaurantId,
    restaurantName,
    isBeachMode,
    setIsBeachMode
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
